import { useNavigate } from "@tanstack/react-router";

import {
  Button,
  DataTable,
  EmptyState,
  Page,
  Panel,
  QuotaMeter,
  Stack,
  StatusBadge,
} from "@/components/ui";

import { Chip } from "../components/Chips";
import { Facts } from "../components/Text";

import { QueryPanel } from "./components/QueryPanel";
import { useEntitlement, useSpacePage } from "./lib/queries";
import { BILLED_QUOTAS, quotaLabel, tallySources } from "./lib/present";

interface QuotaRow {
  name: string;
  label: string;
  limit: number;
  billed: boolean;
}

/** What this account may do right now: capabilities, activities, and every quota with its limit. */
export function ConsolePlan() {
  const navigate = useNavigate();
  const entitlement = useEntitlement();
  const spaces = useSpacePage();

  const quotas = entitlement.data?.quotas ?? {};
  const rows: QuotaRow[] = Object.entries(quotas).map(([name, limit]) => ({
    name,
    label: quotaLabel(name),
    limit,
    billed: BILLED_QUOTAS.includes(name),
  }));

  const spaceLimit = quotas["spaces"];
  const spacesUsed = spaces.data?.spaces?.length ?? 0;

  return (
    <Page
      title="Plan"
      actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              void navigate({ to: "/console/billing" });
            }}
          >
            Billing
          </Button>
      }
    >

      <QueryPanel title="Access" rows={3} query={entitlement}>
        {(held) => (
          <Stack gap={4}>
            <Facts
              items={[
                { label: "Billed plan", value: held.plan_id ?? "none", mono: true },
                { label: "Account", value: held.account_id, mono: true },
                {
                  label: "Sources",
                  value:
                    (held.sources ?? []).length === 0 ? (
                      "none"
                    ) : (
                      <div className="cx-chips">
                        {tallySources(held.sources ?? []).map((source) => (
                          <StatusBadge
                            key={`${source.kind}:${source.plan_id}`}
                            tone={source.kind === "grant" ? "info" : "success"}
                          >
                            {source.count === 1
                              ? `${source.kind} ${source.plan_id}`
                              : `${source.kind} ${source.plan_id} x${String(source.count)}`}
                          </StatusBadge>
                        ))}
                      </div>
                    ),
                },
              ]}
            />
            <div>
              <span className="cx-key">Capabilities</span>
              <div className="cx-chips">
                {(held.capabilities ?? []).length === 0 ? (
                  <span className="cx-muted cx-sm">none</span>
                ) : (
                  (held.capabilities ?? []).map((one) => <Chip key={one}>{one}</Chip>)
                )}
              </div>
            </div>
            <div>
              <span className="cx-key">Activities</span>
              <div className="cx-chips">
                {(held.activities ?? []).length === 0 ? (
                  <span className="cx-muted cx-sm">none</span>
                ) : (
                  (held.activities ?? []).map((one) => <Chip key={one}>{one}</Chip>)
                )}
              </div>
            </div>
          </Stack>
        )}
      </QueryPanel>

      <Panel title="Usage">
        <Stack gap={4}>
          {spaceLimit === undefined ? null : (
            <QuotaMeter label={quotaLabel("spaces")} used={spacesUsed} limit={spaceLimit} />
          )}
          {spaceLimit === undefined ? <EmptyState bare headline="No limits set" /> : null}
        </Stack>
      </Panel>

      <QueryPanel title="Quotas" rows={4} query={entitlement}>
        {() =>
          rows.length === 0 ? (
            <EmptyState bare headline="No quotas" />
          ) : (
            <DataTable
              caption="Quota limits this account resolves to"
              density="comfortable"
              columns={[
                { key: "label", header: "Quota", emphasis: true },
                { key: "name", header: "Name", mono: true, muted: true },
                { key: "limit", header: "Limit", numeric: true },
                {
                  key: "billed",
                  header: "Billed",
                  render: (row: QuotaRow) =>
                    row.billed ? <StatusBadge tone="info">billed</StatusBadge> : null,
                },
              ]}
              rows={rows}
              emptyHeadline="No quotas"
            />
          )
        }
      </QueryPanel>
    </Page>
  );
}
